import { useState, useEffect, useCallback } from 'react';
import { AutoplayMetrics } from '@/store/videoFeatures/types/autoplay-ab-test';

interface UseAutoplayRealTimeMetricsProps {
  selectedTemplates: string[];
  isEnabled: boolean;
  testStatus?: 'active' | 'finished' | 'draft' | 'paused';
}

export const useAutoplayRealTimeMetrics = ({
  selectedTemplates,
  isEnabled,
  testStatus = 'active'
}: UseAutoplayRealTimeMetricsProps) => {
  const [metrics, setMetrics] = useState<AutoplayMetrics[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);

  const generateRealtimeMetrics = useCallback((templateId: string, baseMetrics?: AutoplayMetrics): AutoplayMetrics => {
    // Se já existem métricas base, aplicar pequenas variações
    if (baseMetrics) {
      const variance = 0.05; // 5% de variação
      return {
        ...baseMetrics,
        views: Math.max(baseMetrics.views + Math.floor((Math.random() - 0.5) * baseMetrics.views * variance), 0),
        uniqueViews: Math.max(baseMetrics.uniqueViews + Math.floor((Math.random() - 0.5) * baseMetrics.uniqueViews * variance), 0),
        plays: Math.max(baseMetrics.plays + Math.floor((Math.random() - 0.5) * baseMetrics.plays * variance), 0),
        uniquePlays: Math.max(baseMetrics.uniquePlays + Math.floor((Math.random() - 0.5) * baseMetrics.uniquePlays * variance), 0),
        buttonClicks: Math.max(baseMetrics.buttonClicks + Math.floor((Math.random() - 0.5) * baseMetrics.buttonClicks * variance), 0),
      };
    }

    // Gerar métricas iniciais
    const traffic = Math.floor(100 / selectedTemplates.length);
    const views = Math.floor(Math.random() * 9000) + 1000;
    const uniqueViews = Math.floor(views * (0.7 + Math.random() * 0.2));
    const plays = Math.floor(uniqueViews * (0.3 + Math.random() * 0.4));
    const uniquePlays = Math.floor(plays * (0.8 + Math.random() * 0.15));
    const buttonClicks = Math.floor(plays * (0.1 + Math.random() * 0.3));
    const playRate = Math.round((plays / uniqueViews) * 100);
    const engagement = Math.round((buttonClicks / plays) * 100);

    return {
      templateId,
      templateName: `Template ${templateId}`,
      traffic,
      engagement,
      views,
      uniqueViews,
      plays,
      uniquePlays,
      playRate,
      buttonClicks,
      isEditable: testStatus === 'active',
      performanceIndicator: playRate > 50 ? 'up' : playRate < 30 ? 'down' : 'neutral'
    };
  }, [selectedTemplates.length, testStatus]);

  const updateMetrics = useCallback(() => {
    if (!isEnabled || testStatus === 'finished') return;

    setIsUpdating(true);
    
    // Simular delay de API
    setTimeout(() => {
      setMetrics(prevMetrics => {
        return selectedTemplates.map(templateId => {
          const existingMetric = prevMetrics.find(m => m.templateId === templateId);
          return generateRealtimeMetrics(templateId, existingMetric);
        });
      });
      setIsUpdating(false);
    }, 500);
  }, [isEnabled, testStatus, selectedTemplates, generateRealtimeMetrics]);

  // Inicializar métricas
  useEffect(() => {
    if (selectedTemplates.length > 0) {
      updateMetrics();
    }
  }, [selectedTemplates]);

  // Polling para atualizações em tempo real
  useEffect(() => {
    if (!isEnabled || testStatus === 'finished') return;

    const interval = setInterval(updateMetrics, 10000); // 10 segundos

    return () => clearInterval(interval);
  }, [isEnabled, testStatus, updateMetrics]);

  return {
    metrics,
    isUpdating,
    updateMetrics
  };
};